/**
 * CookieConsentBanner — first-visit consent UI.
 *
 * Renders only when the user has not yet made a consent choice. Offers three
 * actions:
 *   - Accept all       → writes { preferences: true, analytics: true }
 *   - Reject non-essential → writes { preferences: false, analytics: false }
 *   - Manage preferences   → opens the CookiePreferencesModal
 *
 * UX notes:
 *   - Fixed to the bottom of the viewport across all pages.
 *   - Keyboard accessible: focus trap is handled by the Manage modal; the
 *     banner itself is a landmark with role="region" and an aria-label.
 *   - Uses the same design tokens as the rest of the app (CSS custom
 *     properties defined in GlobalStyles).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button } from '@codex/components/inputs';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import CookiePreferencesModal from './CookiePreferencesModal';

const BannerShell = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1200; /* above app content, below MUI Dialog (1300) */
  display: flex;
  justify-content: center;
  padding: var(--spacing-3, 12px);
  pointer-events: none; /* inner Card is the interactive surface */
`;

const BannerCard = styled.div`
  pointer-events: auto;
  width: 100%;
  max-width: 960px;
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary, #0f172a);
  border: 1px solid var(--color-border-default, rgba(0, 0, 0, 0.12));
  border-radius: var(--radius-lg, 12px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  padding: var(--spacing-4, 16px) var(--spacing-5, 20px);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--spacing-4, 16px);
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: var(--color-text-secondary, #475569);

  strong {
    color: var(--color-text-primary, #0f172a);
    font-weight: 600;
  }

  a {
    color: var(--color-primary, #2563eb);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: var(--spacing-2, 8px);
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 720px) {
    justify-content: stretch;

    & > * {
      flex: 1 1 auto;
    }
  }
`;

export default function CookieConsentBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { decided, acceptAll, rejectNonEssential, openPreferences, isPreferencesOpen } =
    useCookieConsent();

  // Always render the modal (it manages its own open state) so "Cookie
  // preferences" links elsewhere on the site work even after the banner has
  // been dismissed.
  return (
    <>
      {!decided && (
        <BannerShell
          role="region"
          aria-label={t('cookies.banner.ariaLabel', { defaultValue: 'Cookie consent' })}
        >
          <BannerCard>
            <Copy>
              <strong>{t('cookies.banner.title', { defaultValue: 'We use cookies' })}</strong>
              <br />
              {t('cookies.banner.body', {
                defaultValue:
                  "We use strictly necessary cookies to keep you signed in. With your consent we'd also like to use preference and analytics cookies to improve the product. You can change your choice any time from the footer.",
              })}{' '}
              <Link to="/legal/cookies">
                {t('cookies.banner.learnMore', { defaultValue: 'Learn more' })}
              </Link>
              .
            </Copy>
            <Actions>
              <Button variant="outlined" size="sm" onClick={openPreferences}>
                {t('cookies.banner.manage', { defaultValue: 'Manage preferences' })}
              </Button>
              <Button variant="outlined" size="sm" onClick={rejectNonEssential}>
                {t('cookies.banner.reject', { defaultValue: 'Reject non-essential' })}
              </Button>
              <Button variant="filled" size="sm" onClick={acceptAll}>
                {t('cookies.banner.accept', { defaultValue: 'Accept all' })}
              </Button>
            </Actions>
          </BannerCard>
        </BannerShell>
      )}
      {/* Modal is always mounted so footer "Cookie preferences" link keeps working. */}
      {isPreferencesOpen && <CookiePreferencesModal />}
    </>
  );
}
